import React, { lazy, ReactElement, Suspense, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { fabric } from 'fabric';
import { ConfirmationDialog, IconNames } from '@dotcom/crown-ui';
import { useAnalyticsContext } from '../../../context/analytics-context';
import {
  setAllDrawersClosed,
  setIsImageEditDrawerOpen,
  setIsResetConfirmationDialogOpen,
  useAppContext,
} from '../../../context/app-context';
import { useUIStore } from '../../../stores/ui';
import { useCropContext } from '../../../context/crop-context';
import { usePhoto } from '../../../features/card-editor/hooks/use-photo';
import { useActiveCanvas, useFeatureFlags } from '../../../hooks';
import { convertPixelToPoint, getGroupedTextObject } from '../../../utils';
import { FontDrawer } from '../font-drawer';
import { FontToolbar } from '../font-toolbar';
import { ImageEditDrawer } from '../image-edit-drawer/image-edit-drawer';
import { ImageEditToolbar } from '../image-edit-toolbar';
import { OrderDrawer } from '../order-drawer';
import { PhotoDrawer } from '../photo-drawer/photo-drawer';
import { RotateDrawer } from '../rotate-drawer';
import { ScaleDrawer } from '../scale-drawer';
import { SizeDrawer } from '../size-drawer/size-drawer';
import { WamInstructionsDrawer } from '../wam-instructions-drawer';
import { WamInstructionsMenu } from '../wam-instructions-menu/wam-instructions-menu';
import styles from './drawer-container.module.scss';
import { useDrawerContainerActions } from './hooks/use-drawer-container-actions';

const ImageUploadDrawerComponent = lazy(() =>
  import('../../card-controls/image-upload-drawer').then((module) => ({
    default: module.ImageUploadDrawer,
  })),
);

export const DrawerContainer = (): ReactElement => {
  const canvas = useActiveCanvas();
  const [initialAngle, setInitialAngle] = React.useState(canvas?.current?.getActiveObject()?.angle ?? 0);
  const isTextDrawerOpen = useUIStore((state) => state.isTextDrawerOpen);
  const {
    appState: {
      isImageUploadDrawerOpen,
      isWamDrawerOpen,
      isRotationDrawerOpen,
      isSizeDrawerOpen,
      isImageEditDrawerOpen,
      isOrderDrawerOpen,
      isScaleDrawerOpen,
      isImageUploadToPhotoZoneOpen,
      isDeleteConfirmationDialogOpen,
      isResetConfirmationDialogOpen,
      isClearConfirmationDialogOpen,
    },
    appDispatch,
  } = useAppContext();
  const { t } = useTranslation();
  const { startCropping, isCropping, finishCropping, cancelCropping } = useCropContext();
  const {
    handleRotateOpenDrawer,
    onChangeOrder,
    openDeleteConfirmationDialog,
    closeDeleteConfirmationDialog,
    closeClearConfirmationDialog,
    onSubmitSize,
    onSubmitRotate,
    onSubmitOrder,
    onFinishCropping,
    onCloseImageEditDrawer,
    onSizeDrawerClose,
    closeResetConfirmationDialog,
    handleClearConfirmation,
    handleResetConfirmation,
    handleDeleteConfirmation,
    handleSizeChange,
    isDeleting,
  } = useDrawerContainerActions(canvas, setInitialAngle);
  const {
    IS_PHOTO_DRAWER_ENABLED,
    IS_NEW_IMAGE_EDIT_TOOLBAR_ENABLED,
    IS_NEW_FONT_TOOLBAR_ENABLED,
    IS_FLOATING_MENU_ENABLED,
  } = useFeatureFlags();
  const { updateEditFormats } = useAnalyticsContext();

  const closeTextDrawer = useUIStore((state) => state.closeTextDrawer);
  const openFontToolbar = useUIStore((state) => state.openFontToolbar);
  // Tracks whether the bridge has already fired for the current isTextDrawerOpen
  // signal. Set to true *before* the store actions so that any rapid async
  // re-trigger hits the guard before openFontToolbar() can be called a second time.
  const hasOpenedFontToolbarRef = useRef(false);
  const closeAllDrawers = useCallback(() => {
    setAllDrawersClosed(appDispatch);
    closeTextDrawer();
  }, [appDispatch, closeTextDrawer]);
  const openImageEditDrawer = useCallback(() => setIsImageEditDrawerOpen(appDispatch), [appDispatch]);

  // TODO: Create a custom hook like this one for each drawer.
  const { addPhotoToCardFace, addPhotoToPhotozone } = usePhoto();

  useEffect(() => {
    setInitialAngle(canvas?.current?.getActiveObject()?.angle ?? 0);
  }, [canvas?.current?.getActiveObject()?.angle]);

  // Bridge: when the feature flag is on, intercept the isTextDrawerOpen signal
  // and open the new FontToolbar instead of the legacy FontDrawer.
  // The ref guard is set before the store actions fire so that even a rapid
  // async re-trigger cannot call openFontToolbar() more than once per signal.
  // It resets when isTextDrawerOpen is false so the next open signal is handled.
  useEffect(() => {
    if (!IS_NEW_FONT_TOOLBAR_ENABLED || !isTextDrawerOpen) {
      hasOpenedFontToolbarRef.current = false;
      return;
    }
    if (hasOpenedFontToolbarRef.current) return;
    hasOpenedFontToolbarRef.current = true;
    closeTextDrawer();
    openFontToolbar();
  }, [IS_NEW_FONT_TOOLBAR_ENABLED, isTextDrawerOpen, closeTextDrawer, openFontToolbar]);

  const OpenDrawer = useMemo(() => {
    if (isTextDrawerOpen) {
      return <FontDrawer />;
    }

    // The new ImageEditToolbar (when the feature flag is enabled) is
    // rendered below for both desktop and mobile and is driven by its own
    // zustand state. When the flag is OFF we fall back to the legacy
    // ImageEditDrawer for the original drawer behaviour.
    if (!IS_NEW_IMAGE_EDIT_TOOLBAR_ENABLED && (isImageEditDrawerOpen || isCropping)) {
      const isHandwriting = canvas?.current?.getActiveObject()?.data?.isPodHandwriting;
      return (
        <ImageEditDrawer
          isOpen={isImageEditDrawerOpen || isCropping}
          onClose={onCloseImageEditDrawer}
          onCrop={startCropping}
          onFinishCropping={onFinishCropping}
          onCancelCropping={cancelCropping}
          isCropping={isCropping}
          onDelete={openDeleteConfirmationDialog}
          onImageSelect={openImageEditDrawer}
          isHandwriting={isHandwriting}
        />
      );
    }

    if (isWamDrawerOpen) {
      const WamComponent = IS_FLOATING_MENU_ENABLED ? WamInstructionsMenu : WamInstructionsDrawer;
      return (
        <WamComponent
          onImageSelect={openImageEditDrawer}
          isOpen={isWamDrawerOpen}
          onCrop={startCropping}
          onClose={closeAllDrawers}
        />
      );
    }

    if (isSizeDrawerOpen) {
      const textObject = getGroupedTextObject(canvas?.current) as fabric.Textbox;
      const textFontSize: number = textObject?.fontSize ?? 30;
      return (
        <div className={styles['drawer-container']}>
          <SizeDrawer
            handleSubmit={onSubmitSize}
            isOpen={isSizeDrawerOpen}
            onClose={() => onSizeDrawerClose(textObject, textFontSize)}
            sizeValue={convertPixelToPoint(textFontSize).toString()}
            sizeOnChange={(valueInRange: string) => handleSizeChange(textObject, valueInRange)}
          />
        </div>
      );
    }

    if (isScaleDrawerOpen) {
      return (
        <div className={styles['drawer-container']}>
          <ScaleDrawer
            isOpen={isScaleDrawerOpen}
            onApply={openImageEditDrawer}
            onChangeCallback={(value) => updateEditFormats({ size: value.toString() })}
            onCancel={openImageEditDrawer}
            minScale={0}
          />
        </div>
      );
    }

    if (isRotationDrawerOpen) {
      return (
        <div className={styles['drawer-container']}>
          <RotateDrawer
            isOpen={isRotationDrawerOpen}
            onDrawerApply={onSubmitRotate}
            onDrawerCancel={handleRotateOpenDrawer}
            initialAngle={initialAngle}
          />
        </div>
      );
    }

    if (isOrderDrawerOpen) {
      return (
        <div className={styles['drawer-container']}>
          <OrderDrawer
            isOpen={isOrderDrawerOpen}
            onClose={openImageEditDrawer}
            onSubmit={onSubmitOrder}
            handleOrderChange={onChangeOrder}
          />
        </div>
      );
    }

    return <React.Fragment />;
  }, [
    isTextDrawerOpen,
    isWamDrawerOpen,
    isRotationDrawerOpen,
    isSizeDrawerOpen,
    isImageEditDrawerOpen,
    isOrderDrawerOpen,
    isScaleDrawerOpen,
    isCropping,
    finishCropping,
    startCropping,
    IS_NEW_IMAGE_EDIT_TOOLBAR_ENABLED,
    IS_FLOATING_MENU_ENABLED,
  ]);

  return (
    <div data-testid="drawer-container">
      {OpenDrawer}
      {IS_NEW_IMAGE_EDIT_TOOLBAR_ENABLED && (
        <ImageEditToolbar
          isHandwriting={Boolean(canvas?.current?.getActiveObject()?.data?.isPodHandwriting)}
          onCrop={startCropping}
          onDelete={openDeleteConfirmationDialog}
          onReset={() => setIsResetConfirmationDialogOpen(appDispatch)}
        />
      )}
      {IS_NEW_FONT_TOOLBAR_ENABLED && <FontToolbar />}
      <Suspense fallback={null}>
        {IS_PHOTO_DRAWER_ENABLED ? (
          <PhotoDrawer />
        ) : (
          <ImageUploadDrawerComponent
            isOpen={isImageUploadDrawerOpen || isImageUploadToPhotoZoneOpen}
            onAddToCardFace={addPhotoToCardFace}
            onAddToPhotoZone={addPhotoToPhotozone}
            onClose={closeAllDrawers}
          />
        )}
      </Suspense>
      <ConfirmationDialog
        id="delete-confirmation-dialog"
        testId="delete-confirmation-dialog"
        isOpen={isDeleteConfirmationDialogOpen}
        onCancel={closeDeleteConfirmationDialog}
        onConfirm={handleDeleteConfirmation}
        title={t('confirmationDialog.delete.title') as string}
        confirmLabel={t('confirmationDialog.delete.confirmButton') as string}
        cancelLabel={t('confirmationDialog.cancelButton') as string}
        isLoading={isDeleting}
        accentIcon={IconNames.CardFeaturesBold}
      >
        {null}
      </ConfirmationDialog>
      <ConfirmationDialog
        id="reset-confirmation-dialog"
        testId="reset-confirmation-dialog"
        isOpen={isResetConfirmationDialogOpen}
        onCancel={closeResetConfirmationDialog}
        onConfirm={handleResetConfirmation}
        title={t('confirmationDialog.resetImage.title') as string}
        confirmLabel={t('confirmationDialog.resetImage.confirmButton') as string}
        cancelLabel={t('confirmationDialog.cancelButton') as string}
        accentIcon={IconNames.CardFeaturesBold}
      >
        {t('confirmationDialog.resetImage.content') as string}
      </ConfirmationDialog>
      <ConfirmationDialog
        id="clear-confirmation-dialog"
        testId="clear-confirmation-dialog"
        isOpen={isClearConfirmationDialogOpen}
        onCancel={closeClearConfirmationDialog}
        onConfirm={handleClearConfirmation}
        title={t('confirmationDialog.clear.title') as string}
        confirmLabel={t('confirmationDialog.clear.confirmButton') as string}
        cancelLabel={t('confirmationDialog.cancelButton') as string}
        accentIcon={IconNames.CardFeaturesBold}
      >
        {null}
      </ConfirmationDialog>
    </div>
  );
};
